import { gql, useQuery } from '@apollo/client';
import { capitalize, noop, orderBy } from 'lodash';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import ExportProjectsCsvButton from './ExportProjectsCsvButton';
import {
  CAPEX_BASIS_METRICS,
  CAPEX_GENETICS_BASIS_METRICS,
} from '../../constants/capexMetrics';
import {
  CropInputAggregateFragment,
  GeneticStockBasisFragment,
  OperationsBasisFragment,
  SiteInfrastructureBasisFragment,
} from '../../queries';
import {
  getGeneticStockBasisData,
  getSiteInfrastructureBasisData,
} from '../../utils/transforms';
import { getOperationsBasisMetrics } from '../../constants/operationsMetrics';
import {
  getOperationItems,
  mergeBasisAggregates,
  occursDuringYearFilter,
  toOperationsTableData,
} from '../../utils/operations';
import { ASSIGNMENTS } from '../../constants/operations';
import { getInputsForYear, metricsForYear } from '../../utils/inputs';
import { getMetricsForYear, getYearlyMetrics } from '../../utils/columns';

const GeneticStockBasisQuery = gql`
  ${GeneticStockBasisFragment}
  query FarmsGeneticStockBasisQuery($ids: [ID!]) {
    farms(ids: $ids) {
      id
      name
      ...GeneticStockBasisFragment
    }
  }
`;

const SiteInfrastructureBasisQuery = gql`
  ${SiteInfrastructureBasisFragment}
  query FarmsSiteInfrastructureBasisQuery($ids: [ID!]) {
    farms(ids: $ids) {
      id
      name
      ...SiteInfrastructureBasisFragment
    }
  }
`;

const OperationsBasisQuery = gql`
  ${OperationsBasisFragment}
  query FarmsOperationsBasisQuery(
    $ids: [ID!]
    $applyBuffer: Boolean!
    $applyMargin: Boolean!
    $applySubsidy: Boolean!
    $assignment: String!
  ) {
    farms(ids: $ids) {
      id
      name
      startYear
      ...OperationsBasisFragment
    }
  }
`;

const InputsBasisQuery = gql`
  ${CropInputAggregateFragment}
  query FarmsInputsBasisQuery($ids: [ID!], $assignment: String!) {
    farms(ids: $ids) {
      id
      name
      startYear
      ...CropInputAggregateFragment
    }
  }
`;

const farmNameColumn = { displayString: 'Farm', accessor: 'farmName' };
const cropNameColumn = {
  displayString: 'Crop',
  accessor: (item) => item.crop?.name,
};

const getFarmCapexBasisData = (
  dataTransform,
  { name: farmName, ...farmData },
) =>
  dataTransform(farmData).map((item) => ({
    farmName,
    ...item,
  }));

const ExportDashboardCostBasisButton = ({
  name, // used for the export filename
  query, // query for multi-farms data
  dataTransform, // function to transform data for a single farm returned by the query
  columns, // columns to export in CSV
  includeCropColumn = false, // include column for crop name after farm name
  variables, // additional GraphQL query variables
  exportType, // string to capture analytics for exports
}) => {
  const {
    dashboards: { dashboardFarmsMap },
  } = useMultiProjectDashboardContext();

  const { refetch: asyncQuery } = useQuery(query, {
    variables: { ids: Object.keys(dashboardFarmsMap), ...variables },
    skip: true,
  });

  const fetchData = async () => {
    const { data } = await asyncQuery();
    return orderBy(data.farms, (f) =>
      f.name.toLowerCase(),
    ).flatMap((farmData) => getFarmCapexBasisData(dataTransform, farmData));
  };

  const filename =
    typeof name === 'function' ? `${name()}.csv` : `${name}CostBasis.csv`;

  return (
    <ExportProjectsCsvButton
      columns={[
        farmNameColumn,
        ...(includeCropColumn ? [cropNameColumn] : []),
        ...columns,
      ]}
      fetchData={fetchData}
      // TODO: For persisted dashboard, add dashboard name to filename?
      filename={filename}
      exportType={exportType || `${name}CostBasis`}
    />
  );
};

export const ExportDashboardGeneticStockBasisButton = () => (
  <ExportDashboardCostBasisButton
    name="geneticStock"
    query={GeneticStockBasisQuery}
    dataTransform={getGeneticStockBasisData}
    columns={CAPEX_GENETICS_BASIS_METRICS}
  />
);

export const ExportDashboardSiteInfrastructureBasisButton = () => (
  <ExportDashboardCostBasisButton
    name="siteInfrastructure"
    query={SiteInfrastructureBasisQuery}
    dataTransform={getSiteInfrastructureBasisData}
    columns={CAPEX_BASIS_METRICS}
  />
);

export const ExportDashboardOperationsBasisButton = ({
  startYear,
  yearIndex,
}) => {
  const columns = [
    {
      displayString: 'Category',
      accessor: 'category',
      format: capitalize,
    },
    ...getOperationsBasisMetrics(null, true, noop),
  ];

  const opMetricsForYear = getMetricsForYear(columns);

  const yearlyMetrics = getYearlyMetrics(columns);

  const dataTransform = (data) => {
    const mergedData = mergeBasisAggregates(data);
    const { cropAggregates } = toOperationsTableData(mergedData);
    return cropAggregates.flatMap(
      getOperationItems(
        null,
        yearIndex,
        null,
        occursDuringYearFilter,
        yearlyMetrics,
        startYear,
      ),
    );
  };

  const variables = {
    applyBuffer: false,
    applyMargin: false,
    applySubsidy: false,
    assignment: ASSIGNMENTS.DEVELOPER,
  };

  return (
    <ExportDashboardCostBasisButton
      name={() => `operationsCostBasis${startYear + yearIndex}`}
      query={OperationsBasisQuery}
      dataTransform={dataTransform}
      columns={opMetricsForYear}
      includeCropColumn
      variables={variables}
      exportType="operationsCostBasis"
    />
  );
};

export const ExportDashboardInputsBasisButton = ({ startYear, yearIndex }) => {
  const dataTransform = (data) => {
    const flattenedData = {
      ...data,
      inputAggregates: data.cropAggregates.flatMap(
        ({ crop, inputAggregates }) =>
          inputAggregates.map((inputAggregate) => ({
            ...inputAggregate,
            crop,
          })),
      ),
    };
    return getInputsForYear(yearIndex, false, startYear)(flattenedData);
  };

  return (
    <ExportDashboardCostBasisButton
      name={() => `inputsCostBasis${startYear + yearIndex}`}
      query={InputsBasisQuery}
      dataTransform={dataTransform}
      columns={metricsForYear(false)}
      includeCropColumn
      variables={{ assignment: ASSIGNMENTS.DEVELOPER }}
      exportType="inputsCostBasis"
    />
  );
};
